import { type FC, useCallback } from "react";

import { Stack } from "@mui/material";
import { FormType, Locales } from "@simplyk/common";
import { UseFormReturn } from "react-hook-form";

import { FundSelectPaper } from "./FundSelectPaper";
import { useFundDesignation } from "./useFundDesignation";

import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";
import { useTranslate } from "@/common/hooks/useTranslate";
import { CreateFundModal } from "@/components/FundModals/CreateFundModal";
import { EditFundModal } from "@/components/FundModals/EditFundModal";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { LiveEditorToggle } from "@/components/LiveEditorElements/LiveEditorToggle/LiveEditorToggle";
import { DashboardAutocomplete } from "@/dashboard-design-system/Autocomplete/DashboardAutocomplete";
import { AutocompleteOption } from "@/dashboard-design-system/Autocomplete/types";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import type { StepAdvancedSettingsFormData } from "@/features/LiveFormEditor/Steps/StepAdvancedSettings/types";
import { Edit } from "@/icons/outlined";

interface FundDesignationCardProps {
  form: UseFormReturn<StepAdvancedSettingsFormData>;
  formId: string;
  formType: FormType;
  locale: Locales;
}

export const FundDesignationCard: FC<FundDesignationCardProps> = ({ form, formId, formType, locale }) => {
  const { t } = useTranslate();
  const { formCategory } = useLiveFormEditorContext();

  const {
    isHidden,
    isEnabled,
    control,
    fundOptions,
    selectedOption,
    organizationFunds,
    isCreateFundOpen,
    editFundData,
    handleToggle,
    handleFundChange,
    handleFundCreated,
    handleEditFund,
    openCreateFund,
    closeCreateFund,
    closeEditFund,
  } = useFundDesignation({ form, formId, formType, locale });

  const paperComponent = useCallback(
    (props: React.ComponentProps<typeof FundSelectPaper>) => (
      <FundSelectPaper {...props} onCreateNewFund={openCreateFund} />
    ),
    [openCreateFund]
  );

  const renderOption = useCallback(
    (props: React.HTMLAttributes<HTMLLIElement>, option: AutocompleteOption<string>) => {
      const fund = organizationFunds?.find((fund) => fund.id === option.value);
      return (
        <li {...props} key={option.value}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
            {option.label}
            {fund && (
              <DashboardIconButton variant="text" size="small" vibe="neutral" onClick={(e) => handleEditFund(e, fund)}>
                <Edit />
              </DashboardIconButton>
            )}
          </Stack>
        </li>
      );
    },
    [organizationFunds, handleEditFund]
  );

  if (isHidden) {
    return null;
  }

  return (
    <>
      <LiveEditorBlock rowGap={1}>
        <LiveEditorToggle
          label={t(
            "dashboard",
            formType === FormType.DonationForm
              ? "funds.designateFullCampaign.donationForm"
              : "funds.designateFullCampaign"
          )}
          tooltip={
            formType === FormType.DonationForm
              ? t("dashboard", "funds.designateFullCampaign.donationForm.tooltip")
              : t(
                  "dashboard",
                  isSweepstakesCategory(formCategory)
                    ? "sweepstakes.funds.designateFullCampaignTooltip"
                    : "funds.designateFullCampaign.tooltip"
                )
          }
          labelVariant="body1"
          name="fundDesignationEnabled"
          control={control}
          onValueChange={handleToggle}
        />

        {isEnabled && (
          <DashboardAutocomplete<AutocompleteOption<string>>
            options={fundOptions}
            value={selectedOption}
            placeholder={t("dashboard", "funds.chooseFund")}
            onChange={handleFundChange}
            disableCloseOnSelect={false}
            PaperComponent={paperComponent}
            renderOption={renderOption}
            hideClearIcon
            sx={{ marginTop: 1 }}
          />
        )}
      </LiveEditorBlock>

      {isCreateFundOpen && <CreateFundModal isOpen onClose={closeCreateFund} onFundCreated={handleFundCreated} />}
      {editFundData && <EditFundModal isOpen onClose={closeEditFund} fund={editFundData} />}
    </>
  );
};
